import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { X } from "lucide-react";
import { ATM_EVENT } from "@/lib/atm";
import { usd } from "@/lib/flip";

const BILLS = Array.from({ length: 9 }, (_, i) => i);

function Atm() {
  return (
    <div className="relative mx-auto h-44 w-40 rounded-t-2xl rounded-b-md bg-gradient-to-b from-slate-700 to-slate-900 shadow-2xl ring-4 ring-slate-950/40" data-testid="atm-machine">
      <div className="mx-auto mt-3 flex h-12 w-28 items-center justify-center rounded-md bg-emerald-950 ring-2 ring-slate-950">
        <span className="atm-screen font-mono text-[11px] font-bold tracking-widest text-emerald-300">$ $ $</span>
      </div>
      <div className="mx-auto mt-2 grid w-24 grid-cols-3 gap-1">
        {Array.from({ length: 9 }, (_, i) => <span key={i} className="h-2.5 rounded-sm bg-slate-500" />)}
      </div>
      <div className="absolute inset-x-5 bottom-5 h-2 rounded-full bg-slate-950" />
      <div className="absolute inset-x-0 bottom-0 h-3 rounded-b-md bg-amber-400 font-mono text-[8px] font-black leading-3 tracking-[0.3em] text-slate-900">SNAPLIST</div>
    </div>
  );
}

// Full-screen payout: the ATM spits bills for a rare find that hits the ROI target.
export default function AtmCelebration() {
  const [hit, setHit] = useState(null);
  useEffect(() => {
    const on = (e) => setHit({ ...e.detail, at: Date.now() });
    window.addEventListener(ATM_EVENT, on);
    return () => window.removeEventListener(ATM_EVENT, on);
  }, []);
  useEffect(() => {
    if (!hit) return undefined;
    const t = setTimeout(() => setHit(null), 5200);
    return () => clearTimeout(t);
  }, [hit]);
  if (!hit) return null;
  return (
    <div className="fixed inset-0 z-[90] flex items-center justify-center bg-slate-950/60 backdrop-blur-sm" onClick={() => setHit(null)} data-testid="atm-celebration" role="alert">
      <style>{`
        @keyframes atm-bill { 0% { transform: translate(0, 0) rotate(0); opacity: 0; } 8% { opacity: 1; } 100% { transform: translate(var(--dx), 340px) rotate(var(--rot)); opacity: 0; } }
        @keyframes atm-pop { 0% { transform: scale(.6) translateY(30px); opacity: 0; } 60% { transform: scale(1.06); opacity: 1; } 100% { transform: scale(1); } }
        @keyframes atm-shake { 0%,100% { transform: translateX(0); } 25% { transform: translateX(-3px) rotate(-1deg); } 75% { transform: translateX(3px) rotate(1deg); } }
        @keyframes atm-blink { 50% { opacity: .35; } }
        .atm-pop { animation: atm-pop .5s cubic-bezier(.2,1.4,.4,1) both; }
        .atm-shake { animation: atm-shake .18s linear 6; }
        .atm-screen { animation: atm-blink .45s steps(2) infinite; }
        .atm-bill { animation: atm-bill 1.6s cubic-bezier(.3,.6,.5,1) infinite both; }
      `}</style>
      <div className="atm-pop relative w-[min(92vw,360px)] text-center" onClick={(e) => e.stopPropagation()}>
        <button type="button" onClick={() => setHit(null)} aria-label="Close" data-testid="atm-close"
          className="absolute -right-1 -top-1 z-10 rounded-full bg-white/90 p-1.5 text-slate-700 shadow transition-transform hover:scale-110"><X className="h-4 w-4" /></button>
        <p className="mb-3 font-mono text-[11px] font-bold uppercase tracking-[0.3em] text-amber-300" data-testid="atm-label">Rare find · {hit.label}</p>
        <div className="relative z-10 mb-5 rounded-2xl bg-white p-4 shadow-xl">
          <p className="text-3xl font-black tracking-tight text-emerald-600" data-testid="atm-profit">+{usd(hit.profit)}</p>
          <p className="text-xs font-semibold text-slate-600">{hit.roi != null ? `${Math.round(hit.roi)}% ROI · ` : ""}beats your target</p>
          {hit.title && <p className="mt-1 truncate text-sm font-medium text-slate-900" data-testid="atm-title">{hit.title}</p>}
          <Link to="/payouts" onClick={() => setHit(null)} data-testid="atm-gallery-link" className="mt-2 inline-block text-[11px] font-semibold text-emerald-700 hover:underline">Saved to your Payout Gallery →</Link>
        </div>
        <div className="atm-shake relative">
          <Atm />
          <div className="pointer-events-none absolute inset-x-0 bottom-4 z-20 h-0">
            {BILLS.map((i) => (
              <span key={`${hit.at}-${i}`} className="atm-bill absolute left-1/2 -ml-7 flex h-7 w-14 items-center justify-center rounded-sm border border-emerald-800 bg-emerald-400 font-mono text-xs font-black text-emerald-950 shadow-lg"
                style={{ "--dx": `${(i - 4) * 26 + (i % 2 ? 10 : -10)}px`, "--rot": `${(i - 4) * 22}deg`, animationDelay: `${i * 0.17}s` }}>$</span>
            ))}
          </div>
        </div>
      </div>
    </div>
  );
}
